/** * メンバープロフィールコンポーネント */
<template>
  <ifield-b-modal
    id="member-profile"
    ref="member-profile"
    body-class="member-profile-dialog-body"
    size="xl"
    centered
    no-body-border
    v-bind:title="$t('theMemberProfile.userInformation')"
    static
    modal-class="detail-dialog-static"
    v-on:show="$_onShowModal"
    v-on:hidden="$_onHidden"
    v-on:shown="$_onShown"
  >
    <loading-mask v-if="isContentCreated" v-bind:isLoading="isLoading">
      <div class="content">
        <!-- header -->
        <member-profile-header
          v-bind:data="userStatus"
          class="header"
          v-on:message-click="$_showMessagePost"
        />
        <!-- panels -->
        <div
          class="panel-container"
          v-bind:class="{
            'first-view': viewIndex === 0,
            'last-view': viewIndex === viewCount - 1,
          }"
          v-bind:style="{ '--index': viewIndex }"
        >
          <div class="panels">
            <component
              v-for="(panel, index) in panels"
              v-bind:key="index"
              v-bind:is="panel.component"
              v-bind="panel.props"
              v-on="panel.listeners"
            />
          </div>
          <template v-if="hasMultiViews">
            <!-- 前/後ボタン -->
            <b-button
              v-if="viewIndex > 0"
              variant="secondary"
              v-ifield-b-tooltip.hover.top
              class="move-button back"
              v-bind:title="leftArrowTooltipTitle"
              v-on:click="$_onBackClick"
            >
              <icon-arrow-down class="arrow" />
            </b-button>
            <b-button
              v-if="viewIndex < viewCount - 1"
              variant="secondary"
              v-ifield-b-tooltip.hover.top
              class="move-button next"
              v-bind:title="rightArrowTooltipTitle"
              v-on:click="$_onNextClick"
            >
              <icon-arrow-down class="arrow" />
            </b-button>
          </template>
        </div>
        <div class="view-indicator" v-if="hasMultiViews">
          <div
            v-for="item in viewIndicators"
            v-bind:key="item.index"
            class="indicator-item"
            v-bind:class="{ active: item.active }"
            v-on:click="viewIndex = item.index"
          />
        </div>
      </div>
    </loading-mask>

    <message-post-dialog
      v-if="isContentCreated"
      ref="message-post-dialog"
      v-bind:default-receive-users="[
        { id: userId, role_id: userStatus?.role_id },
      ]"
    />
    <member-profile-editor
      v-if="isContentCreated"
      ref="member-profile-editor"
      v-on:member-profile-edit-end="$_editEnd"
    />

    <template v-slot:modal-footer>
      <b-button
        size="sm"
        variant="success"
        v-show="isEditable"
        v-t="'theMemberProfile.action.edit'"
        v-on:click="$_editStart"
      />
      <b-button
        size="sm"
        variant="secondary"
        v-t="'theMemberProfile.action.close'"
        v-on:click="$_hideModal"
      />
    </template>
  </ifield-b-modal>
</template>

<script>
import MemberProfileHeader from '@/components/member/AppMemberProfileHeader.vue'
import PanelMemberDetailInfo from '@/components/member/AppPanelMemberDetailInfo.vue'
import PanelMemberDetailMap from '@/components/member/AppPanelMemberDetailMap.vue'
import PanelMemberDetailSensor from '@/components/member/AppPanelMemberDetailSensor.vue'
import IconArrowDown from '@/assets/ifield/icon_arrow_down.svg?component'
import MessagePostDialog from '@/components/message/AppMessagePostDialog.vue'
import MemberProfileEditor from '@/views/TheMemberProfileEditor.vue'
import { FUNCTION_TYPE } from '@/constants.js'
import { RestApiClient } from 'ifield-core-components'
import { toLocalTz, toLocalTzAttribute } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

export default {
  name: 'TheMemberProfile',
  components: {
    'member-profile-header': MemberProfileHeader,
    'icon-arrow-down': IconArrowDown,
    'message-post-dialog': MessagePostDialog,
    'member-profile-editor': MemberProfileEditor,
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // ユーザーID
      userId: null,
      // ロード中の場合はtrue
      isLoading: false,
      /**
       * ユーザーデータ
       * @type {Object}
       */
      userStatus: null,
      /**
       * ビューの表示index
       * @type {Number}
       */
      viewIndex: 0,
      /**
       * ダイアログの中身を生成済みの場合はtrue
       *
       * 地図を作り直さないため、一度開いたら閉じても保持する
       * @type {Boolean}
       */
      isContentCreated: false,
      /**
       * ダイアログを表示中の場合はtrue
       *
       * 非表示の間は地図のリアクティブな描画を止める
       * @type {Boolean}
       */
      isModalShown: false,
    }
  },
  computed: {
    /**
     * 左側パネル移動ボタンのツールチップ
     * @type {String}
     */
    leftArrowTooltipTitle: function () {
      switch (this.viewIndex) {
        case 1:
          return this.$t('theMemberProfile.overview')
        case 2:
          return this.$t('theMemberProfile.map')
        default:
          return ''
      }
    },
    /**
     * 右側パネル移動ボタンのツールチップ
     * @type {String}
     */
    rightArrowTooltipTitle: function () {
      switch (this.viewIndex) {
        case 0:
          return this.$t('theMemberProfile.sensors')
        case 1:
          return this.$t('theMemberProfile.overview')
        default:
          return ''
      }
    },
    /**
     * メンバープロフィールが編集可能かどうか
     * @return {boolean}
     */
    isEditable: function () {
      if (!this.userId) {
        return false
      }
      return this.userId === this.$store.getters.loginState.userId
    },
    /**
     * パネル定義
     * @type {Array.<Object>}
     */
    panels: function () {
      const panel = {
        info: {
          component: PanelMemberDetailInfo,
          props: {
            data: this.userStatus,
          },
        },
        map: {
          component: PanelMemberDetailMap,
          props: {
            data: this.userStatus,
            reactiveRender: this.isModalShown,
          },
        },
        sensor: {
          component: PanelMemberDetailSensor,
          props: {
            data: this.userStatus,
          },
        },
      }
      return this.isSensorAvailable
        ? [panel.info, panel.map, panel.sensor, panel.info]
        : [panel.info, panel.map]
    },
    /**
     * パネル表示ビューの枚数
     * @type {Number}
     */
    viewCount: function () {
      return this.panels.length - 1
    },
    /**
     * 複数のパネルビューがあるか？
     * @type {boolean}
     */
    hasMultiViews: function () {
      return this.viewCount >= 2
    },
    /**
     * パネルビューのインジケーター用データ
     * @type {Array.<Object>}
     */
    viewIndicators: function () {
      return [...Array(this.viewCount)].map((_, index) => ({
        index,
        active: index === this.viewIndex,
      }))
    },
    /**
     * センサーが利用可能か？
     * @type {Boolean}
     */
    isSensorAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SENSOR)
    },
  },
  mounted: function () {
    this.$log.info('memberProfile mounted!')
  },
  beforeDestory: function () {},
  methods: {
    /**
     * 所属グループ別のユーザー絞り込みを実施し、リストと地図の表示をアップデートします。
     * @param {number} userId 表示対象のユーザーID
     */
    showModal: function (userId) {
      this.$log.info('memberProfile showModal', userId)
      this.$_initData()
      this.userId = userId
      this.$_showMemberData()
      this.$refs['member-profile'].show()
    },
    /**
     * データを初期化する
     */
    $_initData: function () {
      this.userId = null
      this.userStatus = null
    },
    /**
     * ビューのbackボタンクリック時処理
     */
    $_onBackClick() {
      this.viewIndex = Math.max(this.viewIndex - 1, 0)
    },
    /**
     * ビューのnextボタンクリック時処理
     */
    $_onNextClick() {
      this.viewIndex = Math.min(this.viewIndex + 1, this.viewCount - 1)
    },
    /**
     * モーダルを閉じる
     */
    $_hideModal: function () {
      this.$refs['member-profile'].hide()
    },
    /**
     * モーダルを表示する際に実行されるメソッド
     */
    $_onShowModal: function () {
      // 地図を作り直さないよう、一度生成した中身は閉じても保持する
      this.isContentCreated = true
      this.isModalShown = true
    },
    /**
     * モーダルを表示した際に実行されるメソッド
     */
    $_onShown: function () {
      // 次のユーザー詳細を表示できるようにするためstoreのIDをクリアする
      this.$store.dispatch('updateProfileUserId', null)
    },
    /**
     * モーダルを閉じた際に実行されるメソッド
     */
    $_onHidden: function () {
      this.isModalShown = false
      this.$emit('member-profile-close')
    },
    /**
     * メッセージ送信モーダルを表示する
     */
    $_showMessagePost: function () {
      this.$refs['message-post-dialog'].showModal()
    },
    /**
     * 編集用モーダルを表示する
     */
    $_editStart: function () {
      this.$log.info('memberProfile editStart')
      this.$refs['member-profile-editor'].showModal(this.userStatus)
    },
    /**
     * 編集用モーダル終了時イベント用
     */
    $_editEnd: function () {
      // ページを再読み込みする
      this.$router.go()
    },
    /**
     * メンバー情報を表示する
     */
    $_showMemberData: async function () {
      this.$_showLoading()

      const url = RestApiClient.createApiUrl(`user/status/${this.userId}`)
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.userStatus = {
          ...json.data,
          last_positioning_at: toLocalTz(json.data.last_positioning_at),
          create_at: toLocalTz(json.data.create_at),
          update_at: toLocalTz(json.data.update_at),
          attributes: json.data.attributes.map((attr) =>
            toLocalTzAttribute(attr)
          ),
        }
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.$_hideLoading()
      }
    },
    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },
    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .member-profile-dialog-body {
  overflow: hidden;
}

.content {
  background-color: #ffffff;
  height: 600px;
  width: 100%;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr) max-content;
}

.header {
  margin-bottom: 16px;
}

.panel-container {
  --index: 0;
  $-border: 1px solid #f0f0f0;

  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
  border-top: $-border;
  border-bottom: $-border;

  &.first-view {
    border-left: $-border;
  }
  &.last-view {
    border-right: $-border;
  }

  $-panel-width: calc((100% - 16px) / 2);

  .panels {
    width: auto;
    height: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    column-gap: 16px;
    padding: 0 16px;

    position: relative;
    left: calc(-1 * var(--index) * #{$-panel-width});
    transition: left ease 0.5s;

    ::v-deep .panel {
      width: $-panel-width;
      flex-shrink: 0;
    }
  }
}

.move-button {
  width: 32px;
  height: 80px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  opacity: 0.5;
  position: absolute;
  top: calc(50% - 40px);
  transition: opacity ease 0.3s;

  &:hover {
    opacity: 1;
    cursor: pointer;
  }

  &:focus,
  &:active {
    box-shadow: none !important;
  }

  &.back {
    left: 2px;
  }

  &.next {
    right: 2px;
  }
}

.arrow {
  fill: none;
  stroke: $entity-color-white;
  width: 48px;
  height: 48px;
  margin: -10px;
  stroke-width: 1.5px;
  stroke-linecap: round;
  stroke-linejoin: round;
  position: relative;

  .back & {
    left: -1px;
    -webkit-transform: rotate(90deg);
    -moz-transform: rotate(90deg);
    -ms-transform: rotate(90deg);
    -o-transform: rotate(90deg);
    transform: rotate(90deg);
  }

  .next & {
    right: -1px;
    -webkit-transform: rotate(-90deg);
    -moz-transform: rotate(-90deg);
    -ms-transform: rotate(-90deg);
    -o-transform: rotate(-90deg);
    transform: rotate(-90deg);
  }
}

.view-indicator {
  width: 100%;
  height: auto;
  margin-top: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 24px;

  .indicator-item {
    width: 8px;
    height: 8px;
    border: 1px solid #707070;
    border-radius: 50%;
    cursor: pointer;

    &.active {
      background-color: #707070;
      cursor: auto;
    }
  }
}
</style>
